import { Target, Eye, ArrowUpRight, Clock, ShieldCheck, Award, Ruler, Download } from "lucide-react";
import { PageHero, CTASection, HistoryTimeline } from "@/components/sections";
import { VALUES, IMG, COMPANY_INTRO, MISSION, VISION } from "@/lib/site";
import { Link } from "@/components/LangLink";
import { CertificationsSection } from "@/components/Certifications";

const WHY_CLOSING = [
  { icon: Clock, title: "Dal 1986", desc: "Oltre quarant'anni dedicati esclusivamente alla terapia elasto-compressiva." },
  { icon: ShieldCheck, title: "100% Specializzati", desc: "Un solo ambito, portato all'eccellenza: solo compressione medicale." },
  { icon: Award, title: "Marchio & Esclusiva", desc: "LUMAVEN di proprietà e SCHIEBLER in distribuzione esclusiva per l'Italia." },
  { icon: Ruler, title: "Misura che conta", desc: "Misurazione professionale, prova della calza e assistenza continuativa." },
];

const ChiSiamo = () => (
  <>
    {/* 1 — HERO */}
    <PageHero
      eyebrow="Chi Siamo"
      title={"Un centro nato\nper prendersi cura."}
      subtitle="Dal 1986 Medisistem è il riferimento napoletano per la terapia elasto-compressiva: competenza, precisione e una relazione che dura nel tempo."
      testid="chisiamo-hero"
    />

    {/* 2 — LA NOSTRA STORIA (narrativa + timeline orizzontale). Statica, sempre visibile. */}
    <section className="py-20 lg:py-28 bg-white" data-testid="chisiamo-story">
      <div className="container-x">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <div className="overflow-hidden rounded-[1.75rem] border border-[#E7EDF2] shadow-[0_30px_70px_-30px_rgba(15,25,35,0.25)]">
              <img src={IMG.clinic} alt="La sede Medisistem" className="w-full h-[480px] object-cover" />
            </div>
          </div>
          <div>
            <span className="eyebrow">La nostra storia</span>
            <h2 className="mt-4 font-serif text-3xl lg:text-4xl font-bold text-navy leading-tight">
              Una storia iniziata nel 1986, diventata Medisistem nel 1992.
            </h2>
            <p className="mt-5 rounded-2xl border border-green/20 bg-green/5 px-5 py-4 font-serif text-lg italic text-green-deep" data-testid="chisiamo-origin">
              Oltre quarant’anni di esperienza nel settore: l’attività del fondatore inizia nel 1986, Medisistem nasce nel 1992.
            </p>
            <div className="mt-6 space-y-4 text-muted-foreground leading-relaxed" data-testid="chisiamo-story-text">
              {COMPANY_INTRO.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
              <p className="font-serif italic text-green text-lg">Affidabilità che si indossa.</p>
            </div>
            <a
              href="/downloads/medisistem-company-profile.pdf"
              download
              data-testid="chisiamo-download-profile"
              className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-green hover:gap-3.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green focus-visible:ring-offset-2"
            >
              <Download size={16} /> Scarica la Company Profile
            </a>
          </div>
        </div>

        {/* Timeline 1992 → 2024 (verticale su mobile, orizzontale su desktop) */}
        <div className="mt-20" data-testid="section-timeline">
          <h3 className="font-serif text-2xl lg:text-3xl font-bold text-navy">Dal 1986 ad oggi</h3>
          <p className="mt-2 text-muted-foreground">Un percorso di specializzazione continua, anno dopo anno.</p>
          <div className="mt-12">
            <HistoryTimeline />
          </div>
        </div>
      </div>
    </section>

    {/* 3 & 4 — MISSIONE / VISIONE (compatte) */}
    <section className="py-16 lg:py-20 bg-cloud" data-testid="chisiamo-mission">
      <div className="container-x grid md:grid-cols-2 gap-5 max-w-5xl">
        <div className="rounded-2xl bg-navy p-6 lg:p-7 text-white">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-green"><Target size={18} /></span>
            <h3 className="font-serif text-lg font-bold">Mission</h3>
          </div>
          <p className="mt-3 text-sm text-white/75 leading-relaxed">{MISSION}</p>
        </div>
        <div className="rounded-2xl bg-green-deep p-6 lg:p-7 text-white">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-white"><Eye size={18} /></span>
            <h3 className="font-serif text-lg font-bold">Vision</h3>
          </div>
          <p className="mt-3 text-sm text-white/85 leading-relaxed">{VISION}</p>
        </div>
      </div>
    </section>

    {/* 5 — VALORI */}
    <section className="py-20 lg:py-28 bg-white" data-testid="chisiamo-values">
      <div className="container-x">
        <div className="max-w-2xl mx-auto text-center">
          <span className="eyebrow">I nostri valori</span>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-bold text-navy">Ciò in cui crediamo.</h2>
        </div>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {VALUES.map((v, i) => (
            <div key={v.title} className="card-clean h-full p-8" data-testid={`value-${i}`}>
              <span className="font-serif text-3xl font-bold text-green/25">0{i + 1}</span>
              <h3 className="mt-3 font-serif text-xl font-bold text-navy">{v.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CERTIFICAZIONI (predisposta, pubblicata solo con CERT_PUBLISHED) */}
    <CertificationsSection />

    {/* 6 — PERCHÉ MEDISISTEM (chiusura) */}
    <section className="py-20 lg:py-28 bg-cloud" data-testid="chisiamo-perche">
      <div className="container-x">
        <div className="max-w-2xl mx-auto text-center">
          <span className="eyebrow">Perché Medisistem</span>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-bold text-navy">Un riferimento che resta nel tempo.</h2>
        </div>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {WHY_CLOSING.map((w) => (
            <div key={w.title} className="card-clean h-full p-7">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-green/10 text-green"><w.icon size={22} /></span>
              <h3 className="mt-5 font-serif text-lg font-bold text-navy">{w.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{w.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 text-center">
          <Link to="/servizi" className="inline-flex items-center gap-2 font-semibold text-green hover:gap-3 transition-all" data-testid="chisiamo-servizi-link">
            Scopri i nostri servizi <ArrowUpRight size={18} />
          </Link>
        </div>
      </div>
    </section>

    <CTASection />
  </>
);

export default ChiSiamo;
